import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import Located from "@mapmycustomers/shared/types/base/Located";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import { EntityPin, PinData } from "@mapmycustomers/shared/types/map";
import { convertLatLngToCoordinates } from "@mapmycustomers/shared/util/geo/GeoService";

import { Feature } from "@app/component/map/FeaturedMap";
import AlertBadgesType from "@app/enum/map/AlertBadgesType";
import Legend from "@app/scene/map/enums/Legend";
import { getLayerLegendsState, getVisibleBadges } from "@app/scene/map/store/layers/selectors";
import { geocodePinLocation } from "@app/scene/map/store/pinLocation/actions";
import {
  getPinLocationEntity,
  getPinLocationEntityId,
  getPinLocationLocated,
} from "@app/scene/map/store/pinLocation/selectors";
import { getMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { FOCUSED_ENTITY_ZINDEX } from "@app/util/map/consts";
import defaultMapEntryGeometryGetter from "@app/util/map/defaultMapEntryGeometryGetter";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";
import typedIdGetter from "@app/util/typedIdGetter";

interface Props {
  areBadgesVisible?: boolean;
  entity?: EntityWithLocation;
  entityId?: string;
  located?: Located;
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onGeocodePinLocation: typeof geocodePinLocation.request;
  visibleBadges: AlertBadgesType[];
}

const PinLocationEntityFeature: React.FC<Props> = ({
  areBadgesVisible,
  entity,
  entityId,
  located,
  mapSettings,
  onGeocodePinLocation,
  visibleBadges,
}) => {
  const handleMouseUp = useCallback(
    (pin: EntityPin, event: google.maps.MapMouseEvent) => {
      if (mapPinIdGetter(pin) === entityId && event.latLng) {
        onGeocodePinLocation(convertLatLngToCoordinates(event.latLng));
      }
    },
    [onGeocodePinLocation, entityId]
  );

  const entityIconGetter = useMemo(
    () => (entityPin: EntityPin) => mapSettings.showEntityIcons ? entityPin.data.entity : undefined,
    [mapSettings.showEntityIcons]
  );

  const entityPinStyleGetter = useMemo(() => {
    return defaultEntityStyleGetter({
      areBadgesVisible,
      draggable: true,
      icon: entityIconGetter,
      markerSize: mapSettings.markerSize,
      shape: (entityPin: EntityPin) => entityPin.data.shape ?? MarkerShape.SQUARE,
      showEntityIcon: mapSettings.showEntityIcons,
      visibleBadges,
      zIndex: FOCUSED_ENTITY_ZINDEX,
    });
  }, [areBadgesVisible, entityIconGetter, mapSettings, visibleBadges]);

  const pin = useMemo(() => {
    if (entity) {
      const coordinates = located?.geoPoint?.coordinates ?? entity.geoPoint?.coordinates ?? [0, 0];
      return {
        id: entityId,
        bounds: {
          bottom_right: coordinates,
          top_left: coordinates,
        },
        data: entity as PinData,
        multiPin: false,
        region: {
          latitude: coordinates[1],
          latitudeDelta: 0,
          longitude: coordinates[0],
          longitudeDelta: 0,
        },
      } as EntityPin;
    }
  }, [entity, entityId, located]);

  if (!pin) {
    return null;
  }

  return (
    <Feature<EntityPin>
      geometryGetter={defaultMapEntryGeometryGetter}
      idGetter={typedIdGetter}
      items={[pin]}
      onMouseUp={handleMouseUp}
      styleGetter={entityPinStyleGetter}
      type="pinLocationEntity"
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  areBadgesVisible: getLayerLegendsState(state)[Legend.ALERTS]?.visible,
  entity: getPinLocationEntity(state),
  entityId: getPinLocationEntityId(state),
  located: getPinLocationLocated(state),
  mapSettings: getMapSettings(state),
  visibleBadges: getVisibleBadges(state),
});

const mapDispatchToProps = {
  onGeocodePinLocation: geocodePinLocation.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(PinLocationEntityFeature);
